/**
 * R&D Invoice — KOSTRA Hospitality Pvt. Ltd.
 *
 * Single-page invoice builder covering Modules 1-10, 12-14 of the spec:
 *   client info · consultancy type & sub-category · project details ·
 *   service billing matrix · additional charges · custom conditions ·
 *   milestone billing · tax/TDS config · payment details · status workflow ·
 *   PDF + Excel downloads · saved invoices side-rail.
 *
 * Live totals computed on the client; backend recomputes authoritatively on save.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash, DownloadSimple, FilePdf, FileXls, Receipt,
         Briefcase, UserCircle, Tag, ChartLine, Coins, ClipboardText,
         Sparkle, FloppyDisk, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

import { api, API, fmtINR, TOKEN_KEY } from "../lib/kostra";
import { useAuth } from "../lib/auth";

// =============================================================================
// Static dropdown data
// =============================================================================
const CONSULTANCY_TYPES = [
  "Product Development Consultancy", "Recipe Development", "Food Technology Consultancy",
  "Manufacturing Consultancy", "Third-Party Manufacturing Support", "Product Testing & Optimization",
  "Packaging Consultancy", "Label Compliance Consultancy", "Brand Development Consultancy",
  "Market Research Consultancy", "Menu Engineering Consultancy", "Food Costing Consultancy",
  "Kitchen Setup Consultancy", "Factory Setup Consultancy", "SOP Development Consultancy",
  "Franchise Development Consultancy", "D2C Brand Launch Consultancy", "Investor Pitch Consultancy",
  "Retainer Consultancy", "Custom Consultancy",
];

const SUBCATEGORIES = {
  "Product Development Consultancy": [
    "Protein Nachos", "Healthy Snacks", "Makhana Products", "Gummies",
    "Bakery Products", "Ready-to-Eat Products", "Functional Foods",
    "Beverages", "Nutraceutical Products", "Other",
  ],
  "Manufacturing Consultancy": [
    "Vendor Identification", "Commercial Trial Support", "Production Scale-Up",
    "Factory Setup", "SOP Creation",
  ],
  "Brand Development Consultancy": [
    "Product Positioning", "Packaging Design", "Pricing Strategy",
    "GTM Strategy", "Marketplace Onboarding",
  ],
};

const UNITS = ["Per SKU", "Per Project", "Per Day", "Per Visit", "Per Hour", "Lump Sum", "Retainer"];
const STATUSES = ["Draft", "Pending Approval", "Approved", "Invoice Generated",
                  "Payment Pending", "Partially Paid", "Paid", "Closed"];
const DOC_TITLES = ["Quotation", "Proforma Invoice", "Tax Invoice",
                    "Credit Note", "Debit Note", "Payment Reminder", "Final Settlement Invoice"];
const MILESTONE_PRESETS = ["Option 1", "Option 2", "Option 3", "Custom"];

const PRESET_DESCRIPTIONS = {
  "Option 1": "50 % Advance · 30 % Product Approval · 20 % Final Delivery",
  "Option 2": "100 % Advance",
  "Option 3": "Monthly Retainer",
  "Custom":   "Define your own milestones below",
};

// =============================================================================
// Blank state helpers
// =============================================================================
const blankClient = () => ({
  client_name: "", company_name: "", contact_person: "", mobile: "", email: "",
  gst_number: "", pan_number: "", billing_address: "",
  project_location: "", referral_source: "",
});
const blankProject = () => ({
  project_name: "", project_id: "", skus_count: 0, start_date: "",
  estimated_completion: "", consultant_assigned: "", priority: "Medium",
});
const blankService = () => ({
  id: crypto.randomUUID(), category: "", description: "",
  quantity: 1, unit: "Per Project", rate: 0, gst_pct: 18,
});
const blankCharge = () => ({
  id: crypto.randomUUID(), name: "", description: "", amount: 0, gst_applicable: true,
});
const blankMilestone = () => ({ name: "", pct: 0, trigger: "" });
const blankInvoice = () => ({
  title: "Quotation",
  doc_type: "quotation",
  client: blankClient(),
  consultancy_type: "",
  consultancy_subcategory: "",
  project: blankProject(),
  services: [blankService()],
  additional_charges: [],
  conditions: [""],
  milestone_preset: "Option 1",
  milestones: [],
  tax: { gst_applicable: true, gst_rate: 18, tds_applicable: false, tds_rate: 10, reverse_charge: false },
  payment: {
    bank_name: "", account_name: "KOSTRA Hospitality Pvt. Ltd.",
    account_number: "", ifsc: "", upi_id: "",
    terms: "50% advance, 30% on product approval, 20% on final delivery.",
  },
  signatory: { name: "", designation: "Authorized Signatory", date: "", signature_data_url: "" },
  status: "Draft",
  notes: "",
});

// =============================================================================
// Client-side totals (mirrors backend for live preview)
// =============================================================================
function computeTotals(inv) {
  const services = (inv.services || []).map((s) => ({
    ...s, amount: Math.round((Number(s.quantity) || 0) * (Number(s.rate) || 0) * 100) / 100,
  }));
  const serviceSub = services.reduce((a, s) => a + s.amount, 0);
  const ach = inv.additional_charges || [];
  const gstableCharges = ach.filter((c) => c.gst_applicable !== false)
                          .reduce((a, c) => a + (Number(c.amount) || 0), 0);
  const nonGstCharges = ach.filter((c) => c.gst_applicable === false)
                          .reduce((a, c) => a + (Number(c.amount) || 0), 0);
  const subtotal = serviceSub + gstableCharges + nonGstCharges;
  const gstBase = serviceSub + gstableCharges;
  const gstAmount = inv.tax?.gst_applicable
    ? Math.round(gstBase * (Number(inv.tax?.gst_rate) || 0)) / 100
    : 0;
  const tdsAmount = inv.tax?.tds_applicable
    ? Math.round(subtotal * (Number(inv.tax?.tds_rate) || 0)) / 100
    : 0;
  const grand = subtotal + gstAmount - tdsAmount;
  return {
    services,
    service_subtotal: serviceSub,
    charges_subtotal: gstableCharges + nonGstCharges,
    subtotal,
    gst_base: gstBase,
    gst_amount: gstAmount,
    tds_amount: tdsAmount,
    grand_total: grand,
  };
}

// =============================================================================
// Page
// =============================================================================
export default function InvoicePage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [inv, setInv] = useState(blankInvoice());
  const [saved, setSaved] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [nextNumber, setNextNumber] = useState("");
  const [busy, setBusy] = useState(false);

  // =============== load saved list ============================================
  const loadAll = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [{ data: list }, { data: nn }] = await Promise.all([
        api.get("/invoices"),
        api.get("/invoices/next-number"),
      ]);
      setSaved(list || []);
      setNextNumber(nn?.next_number || "");
    } catch (e) { /* silent */ }
  }, [isAdmin]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // =============== mutators ===================================================
  const setField = (path, value) =>
    setInv((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...cur[parts[i]] };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  const setService = (idx, patch) =>
    setInv((prev) => ({ ...prev, services: prev.services.map((s, i) => i === idx ? { ...s, ...patch } : s) }));
  const addService = () => setInv((prev) => ({ ...prev, services: [...prev.services, blankService()] }));
  const removeService = (idx) => setInv((prev) => ({ ...prev, services: prev.services.filter((_, i) => i !== idx) }));
  const setCharge = (idx, patch) =>
    setInv((prev) => ({ ...prev, additional_charges: prev.additional_charges.map((c, i) => i === idx ? { ...c, ...patch } : c) }));
  const addCharge = () => setInv((prev) => ({ ...prev, additional_charges: [...prev.additional_charges, blankCharge()] }));
  const removeCharge = (idx) =>
    setInv((prev) => ({ ...prev, additional_charges: prev.additional_charges.filter((_, i) => i !== idx) }));
  const setCondition = (idx, value) =>
    setInv((prev) => ({ ...prev, conditions: prev.conditions.map((c, i) => i === idx ? value : c) }));
  const addCondition = () => setInv((prev) => ({ ...prev, conditions: [...prev.conditions, ""] }));
  const removeCondition = (idx) =>
    setInv((prev) => ({ ...prev, conditions: prev.conditions.filter((_, i) => i !== idx) }));
  const setMilestone = (idx, patch) =>
    setInv((prev) => ({ ...prev, milestones: prev.milestones.map((m, i) => i === idx ? { ...m, ...patch } : m) }));
  const addMilestone = () =>
    setInv((prev) => ({ ...prev, milestones: [...(prev.milestones || []), blankMilestone()] }));
  const removeMilestone = (idx) =>
    setInv((prev) => ({ ...prev, milestones: prev.milestones.filter((_, i) => i !== idx) }));

  // =============== live totals ================================================
  const totals = useMemo(() => computeTotals(inv), [inv]);

  // =============== signature upload ==========================================
  const onSignatureUpload = (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please upload an image file (PNG/JPG)");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Signature image must be under 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setField("signatory.signature_data_url", reader.result);
    reader.onerror = () => toast.error("Failed to read signature image");
    reader.readAsDataURL(file);
  };

  // =============== actions ====================================================
  const buildPayload = () => ({
    ...inv,
    // Strip blank conditions to keep saved data tidy
    conditions: (inv.conditions || []).map((c) => (c || "").trim()).filter(Boolean),
    milestones: inv.milestone_preset === "Custom" ? (inv.milestones || []) : [],
  });

  const saveInvoice = async () => {
    if (!isAdmin) return;
    if (!inv.client.company_name && !inv.client.client_name) {
      toast.error("Please enter client / company name");
      return;
    }
    if (!inv.services?.length || inv.services.every((s) => !s.category && !s.description)) {
      toast.error("Add at least one service line");
      return;
    }
    setBusy(true);
    try {
      const url = editingId ? `/invoices/${editingId}` : "/invoices";
      const method = editingId ? "put" : "post";
      const { data } = await api[method](url, buildPayload());
      setEditingId(data.id);
      toast.success(editingId ? "Invoice updated" : `Invoice ${data.invoice_number} created`);
      // Re-hydrate from server so totals + project_id + invoice_number reflect server truth
      setInv((prev) => ({
        ...prev,
        project: { ...prev.project, project_id: data.project?.project_id || prev.project.project_id },
      }));
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const loadInvoice = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv({
        title: data.title || "Quotation",
        doc_type: data.doc_type || "quotation",
        client: { ...blankClient(), ...(data.client || {}) },
        consultancy_type: data.consultancy_type || "",
        consultancy_subcategory: data.consultancy_subcategory || "",
        project: { ...blankProject(), ...(data.project || {}) },
        services: (data.services || []).map((s) => ({ ...s, id: s.id || crypto.randomUUID() })),
        additional_charges: (data.additional_charges || []).map((c) => ({ ...c, id: c.id || crypto.randomUUID() })),
        conditions: (data.conditions && data.conditions.length) ? data.conditions : [""],
        milestone_preset: data.milestone_preset || "Option 1",
        milestones: data.milestones || [],
        tax: { ...blankInvoice().tax, ...(data.tax || {}) },
        payment: { ...blankInvoice().payment, ...(data.payment || {}) },
        signatory: { ...blankInvoice().signatory, ...(data.signatory || {}) },
        status: data.status || "Draft",
        notes: data.notes || "",
      });
      setEditingId(id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Failed to load invoice");
    }
  };

  const newInvoice = () => { setInv(blankInvoice()); setEditingId(null); loadAll(); };

  const deleteInvoice = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      if (editingId === id) newInvoice();
      loadAll();
    } catch (e) { toast.error("Delete failed"); }
  };

  const cloneAs = async (id, doc_type, label) => {
    try {
      const { data } = await api.post(`/invoices/${id}/clone-as`, { doc_type });
      toast.success(`${label} ${data.invoice_number} created`);
      await loadAll();
      loadInvoice(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not create ${label}`);
    }
  };

  const download = async (fmt) => {
    if (!editingId) { toast.error("Save the invoice first to download"); return; }
    try {
      const resp = await fetch(`${API}/invoices/${editingId}/export/${fmt}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.client.company_name || "invoice"}_${editingId.slice(0, 6)}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(`${fmt.toUpperCase()} download failed: ${e.message}`); }
  };

  const previewPDF = async () => {
    if (!editingId) { toast.error("Save the invoice first to preview"); return; }
    try {
      const resp = await fetch(`${API}/invoices/${editingId}/export/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      // Open in new tab as inline PDF
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Pop-up blocked — please allow pop-ups to preview");
      // Defer cleanup so the new tab has time to load
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(`Preview failed: ${e.message}`); }
  };

  // =============== render =====================================================
  if (!isAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <Receipt size={48} className="mx-auto text-[#A87C0E] mb-4" weight="duotone" />
          <h2 className="text-2xl font-bold text-[#0A3B24] mb-2">R&D Invoice</h2>
          <p className="text-sm text-[#4B5563] mb-4">Admin login required to generate and download invoices.</p>
          <Button onClick={() => navigate("/")} data-testid="invoice-guest-back-btn">Back to Home</Button>
        </div>
      </div>
    );
  }

  const subcatOptions = SUBCATEGORIES[inv.consultancy_type] || null;

  return (
    <div className="px-4 sm:px-6 lg:px-10 pb-16 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="pt-6 pb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#A87C0E] font-bold">
            <Receipt size={11} weight="bold" className="inline mr-1" /> R&D Invoice · KOSTRA Hospitality Pvt. Ltd.
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0A3B24] mt-1">
            Automated invoice generator
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">
            For Product Development, Manufacturing, Brand Launch, and Retainer Services. Quotation, Proforma, Tax Invoice, Credit Note — all in one. PDF + Excel one-click.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">Next #</div>
          <div className="font-mono-num font-bold text-[#A87C0E]" data-testid="invoice-next-number">{nextNumber || "—"}</div>
          {editingId && (
            <Badge variant="outline" className="mt-1 text-[10px] border-emerald-400 text-emerald-700">
              Editing
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* ============================== Main form ============================== */}
        <div className="space-y-5">
          {/* Document meta */}
          <Card title="Document" icon={<ClipboardText size={14} weight="bold" />}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Title">
                <Select value={inv.title} onValueChange={(v) => setField("title", v)}>
                  <SelectTrigger className="h-11" data-testid="invoice-title-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_TITLES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={inv.status} onValueChange={(v) => setField("status", v)}>
                  <SelectTrigger className="h-11" data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </Field>
              <Field label="Project ID (auto)">
                <Input value={inv.project.project_id || "Auto-assigned on save"} disabled className="h-11 font-mono-num bg-[#F9FAFB]" />
              </Field>
            </div>
          </Card>

          {/* Client */}
          <Card title="Client Information" icon={<UserCircle size={14} weight="bold" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Client Name"><Input value={inv.client.client_name} onChange={(e) => setField("client.client_name", e.target.value)} data-testid="client-client-name" /></Field>
              <Field label="Company Name"><Input value={inv.client.company_name} onChange={(e) => setField("client.company_name", e.target.value)} data-testid="client-company-name" /></Field>
              <Field label="Contact Person"><Input value={inv.client.contact_person} onChange={(e) => setField("client.contact_person", e.target.value)} data-testid="client-contact" /></Field>
              <Field label="Mobile Number"><Input value={inv.client.mobile} onChange={(e) => setField("client.mobile", e.target.value)} data-testid="client-mobile" /></Field>
              <Field label="Email ID"><Input value={inv.client.email} onChange={(e) => setField("client.email", e.target.value)} data-testid="client-email" /></Field>
              <Field label="GST Number"><Input value={inv.client.gst_number} onChange={(e) => setField("client.gst_number", e.target.value)} data-testid="client-gst" /></Field>
              <Field label="PAN Number"><Input value={inv.client.pan_number} onChange={(e) => setField("client.pan_number", e.target.value)} data-testid="client-pan" /></Field>
              <Field label="Project Location"><Input value={inv.client.project_location} onChange={(e) => setField("client.project_location", e.target.value)} data-testid="client-location" /></Field>
              <Field label="Billing Address" className="md:col-span-2">
                <Textarea rows={2} value={inv.client.billing_address} onChange={(e) => setField("client.billing_address", e.target.value)} data-testid="client-address" />
              </Field>
              <Field label="Referral Source" className="md:col-span-2"><Input value={inv.client.referral_source} onChange={(e) => setField("client.referral_source", e.target.value)} /></Field>
            </div>
          </Card>

          {/* Consultancy + Project */}
          <Card title="Consultancy & Project" icon={<Briefcase size={14} weight="bold" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Consultancy Type">
                <Select value={inv.consultancy_type || ""} onValueChange={(v) => { setField("consultancy_type", v); setField("consultancy_subcategory", ""); }}>
                  <SelectTrigger className="h-11" data-testid="consultancy-type-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{CONSULTANCY_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                </Select>
              </Field>
              <Field label="Sub-Category">
                {subcatOptions ? (
                  <Select value={inv.consultancy_subcategory || ""} onValueChange={(v) => setField("consultancy_subcategory", v)}>
                    <SelectTrigger className="h-11" data-testid="consultancy-subcategory-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{subcatOptions.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                  </Select>
                ) : (
                  <Input value={inv.consultancy_subcategory} onChange={(e) => setField("consultancy_subcategory", e.target.value)}
                         placeholder={inv.consultancy_type ? "(optional free text)" : "Pick a consultancy type first"} />
                )}
              </Field>
              <Field label="Project Name"><Input value={inv.project.project_name} onChange={(e) => setField("project.project_name", e.target.value)} data-testid="project-name" /></Field>
              <Field label="Consultant Assigned"><Input value={inv.project.consultant_assigned} onChange={(e) => setField("project.consultant_assigned", e.target.value)} /></Field>
              <Field label="Number of SKUs"><Input type="number" min="0" value={inv.project.skus_count} onChange={(e) => setField("project.skus_count", parseInt(e.target.value || 0))} /></Field>
              <Field label="Priority">
                <Select value={inv.project.priority} onValueChange={(v) => setField("project.priority", v)}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{["High", "Medium", "Low"].map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </Field>
              <Field label="Project Start Date"><Input type="date" value={inv.project.start_date} onChange={(e) => setField("project.start_date", e.target.value)} /></Field>
              <Field label="Estimated Completion"><Input type="date" value={inv.project.estimated_completion} onChange={(e) => setField("project.estimated_completion", e.target.value)} /></Field>
            </div>
          </Card>

          {/* Service Billing Matrix */}
          <Card title="Service Billing Matrix" icon={<Tag size={14} weight="bold" />}
                action={<Button size="sm" variant="outline" onClick={addService} data-testid="add-service-btn"><Plus size={14} className="mr-1" />Add Service</Button>}>
            {inv.services.map((s, idx) => (
              <div key={s.id} className="relative pr-9 mb-3 pb-3 border-b border-dashed border-[#F3F4F6] last:border-b-0 last:pb-0 last:mb-0 grid grid-cols-12 gap-2 items-end" data-testid={`service-row-${idx}`}>
                <div className="col-span-12 md:col-span-3">
                  <Label className="kostra-data-label">Category</Label>
                  <Input value={s.category} onChange={(e) => setService(idx, { category: e.target.value })} className="h-10" data-testid={`service-category-${idx}`} />
                </div>
                <div className="col-span-12 md:col-span-3">
                  <Label className="kostra-data-label">Description</Label>
                  <Input value={s.description} onChange={(e) => setService(idx, { description: e.target.value })} className="h-10" data-testid={`service-desc-${idx}`} />
                </div>
                <div className="col-span-3 md:col-span-1">
                  <Label className="kostra-data-label">Qty</Label>
                  <Input type="number" min="0" step="0.5" value={s.quantity} onChange={(e) => setService(idx, { quantity: parseFloat(e.target.value || 0) })} className="h-10 font-mono-num" data-testid={`service-qty-${idx}`} />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Label className="kostra-data-label">Unit</Label>
                  <Select value={s.unit} onValueChange={(v) => setService(idx, { unit: v })}>
                    <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-3 md:col-span-2">
                  <Label className="kostra-data-label">Rate (₹)</Label>
                  <Input type="number" min="0" step="0.5" value={s.rate} onChange={(e) => setService(idx, { rate: parseFloat(e.target.value || 0) })} className="h-10 font-mono-num" data-testid={`service-rate-${idx}`} />
                </div>
                <div className="col-span-2 md:col-span-1 text-right">
                  <Label className="kostra-data-label">Amount</Label>
                  <div className="h-10 flex items-center justify-end font-mono-num font-bold text-[#0A3B24]" data-testid={`service-amount-${idx}`}>
                    {fmtINR((Number(s.quantity) || 0) * (Number(s.rate) || 0))}
                  </div>
                </div>
                <button type="button" onClick={() => removeService(idx)}
                        className="absolute top-0 right-0 h-7 w-7 inline-flex items-center justify-center rounded-md text-[#9CA3AF] hover:text-rose-500 hover:bg-rose-50 transition-colors"
                        title="Remove service" data-testid={`service-remove-${idx}`}>
                  <Trash size={14} />
                </button>
              </div>
            ))}
            {!inv.services.length && (
              <div className="text-xs text-[#6B7280] italic py-2">No services yet. Click <b>Add Service</b>.</div>
            )}
          </Card>

          {/* Additional Charges */}
          <Card title="Additional Charges" icon={<Coins size={14} weight="bold" />}
                action={<Button size="sm" variant="outline" onClick={addCharge} data-testid="add-charge-btn"><Plus size={14} className="mr-1" />Add Charge</Button>}>
            {inv.additional_charges.length === 0 && (
              <div className="text-xs text-[#6B7280] italic py-2">Add Trial Cost, Travel, Packaging Samples, etc.</div>
            )}
            {inv.additional_charges.map((c, idx) => (
              <div key={c.id} className="relative pr-9 mb-3 pb-3 border-b border-dashed border-[#F3F4F6] last:border-b-0 last:pb-0 last:mb-0 grid grid-cols-12 gap-2 items-end" data-testid={`charge-row-${idx}`}>
                <div className="col-span-12 md:col-span-3">
                  <Label className="kostra-data-label">Charge Name</Label>
                  <Input value={c.name} onChange={(e) => setCharge(idx, { name: e.target.value })} className="h-10" data-testid={`charge-name-${idx}`} />
                </div>
                <div className="col-span-12 md:col-span-5">
                  <Label className="kostra-data-label">Description</Label>
                  <Input value={c.description} onChange={(e) => setCharge(idx, { description: e.target.value })} className="h-10" />
                </div>
                <div className="col-span-8 md:col-span-3">
                  <Label className="kostra-data-label">Amount (₹)</Label>
                  <Input type="number" min="0" step="0.5" value={c.amount} onChange={(e) => setCharge(idx, { amount: parseFloat(e.target.value || 0) })} className="h-10 font-mono-num" data-testid={`charge-amount-${idx}`} />
                </div>
                <div className="col-span-4 md:col-span-1">
                  <Label className="kostra-data-label">GST?</Label>
                  <div className="h-10 flex items-center">
                    <Switch checked={c.gst_applicable !== false} onCheckedChange={(v) => setCharge(idx, { gst_applicable: v })} />
                  </div>
                </div>
                <button type="button" onClick={() => removeCharge(idx)}
                        className="absolute top-0 right-0 h-7 w-7 inline-flex items-center justify-center rounded-md text-[#9CA3AF] hover:text-rose-500 hover:bg-rose-50 transition-colors"
                        title="Remove charge" data-testid={`charge-remove-${idx}`}>
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </Card>

          {/* Conditions */}
          <Card title="Additional Scope / Special Conditions" icon={<Sparkle size={14} weight="bold" />}
                action={<Button size="sm" variant="outline" onClick={addCondition} data-testid="add-condition-btn"><Plus size={14} className="mr-1" />Add Point</Button>}>
            {inv.conditions.map((cond, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <Input value={cond} onChange={(e) => setCondition(idx, e.target.value)}
                       placeholder="e.g. Shelf life study not included." data-testid={`condition-${idx}`} />
                <Button variant="ghost" size="icon" onClick={() => removeCondition(idx)} className="text-[#9CA3AF] hover:text-rose-500 h-10 w-10"><Trash size={14} /></Button>
              </div>
            ))}
          </Card>

          {/* Milestone Billing */}
          <Card title="Milestone Billing" icon={<ChartLine size={14} weight="bold" />}>
            <div className="flex flex-wrap gap-2">
              {MILESTONE_PRESETS.map((p) => (
                <button key={p} type="button"
                        onClick={() => setField("milestone_preset", p)}
                        className={`px-3 py-1.5 rounded-full border text-xs font-bold transition-colors ${
                          inv.milestone_preset === p
                            ? "bg-[#0A3B24] text-white border-[#0A3B24]"
                            : "bg-white text-[#0A3B24] border-[#E5E7EB] hover:border-[#0A3B24]"}`}
                        data-testid={`milestone-preset-${p.replace(/\s/g, "-")}`}
                >{p}</button>
              ))}
            </div>
            <div className="text-xs text-[#4B5563] mt-2 font-mono-num">
              {PRESET_DESCRIPTIONS[inv.milestone_preset]}
            </div>
            {inv.milestone_preset === "Custom" && (
              <>
                <div className="mt-3 space-y-2">
                  {(inv.milestones || []).map((m, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-12 md:col-span-4">
                        <Label className="kostra-data-label">Milestone Name</Label>
                        <Input value={m.name} onChange={(e) => setMilestone(idx, { name: e.target.value })} className="h-10" />
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <Label className="kostra-data-label">% of Total</Label>
                        <Input type="number" min="0" max="100" step="0.5" value={m.pct} onChange={(e) => setMilestone(idx, { pct: parseFloat(e.target.value || 0) })} className="h-10 font-mono-num" />
                      </div>
                      <div className="col-span-8 md:col-span-5">
                        <Label className="kostra-data-label">Trigger Event</Label>
                        <Input value={m.trigger} onChange={(e) => setMilestone(idx, { trigger: e.target.value })} className="h-10" />
                      </div>
                      <div className="col-span-12 md:col-span-1 flex justify-end">
                        <Button variant="ghost" size="icon" onClick={() => removeMilestone(idx)} className="text-[#9CA3AF] hover:text-rose-500 h-9 w-9"><Trash size={14} /></Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" onClick={addMilestone} className="mt-2"><Plus size={14} className="mr-1" />Add Milestone</Button>
              </>
            )}
          </Card>

          {/* Tax + Payment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Card title="Tax Configuration" icon={<Coins size={14} weight="bold" />}>
              <div className="space-y-3">
                <ToggleRow label="GST Applicable" checked={inv.tax.gst_applicable} onChange={(v) => setField("tax.gst_applicable", v)} />
                <Field label="GST Rate (%)">
                  <Input type="number" min="0" max="100" step="0.5" value={inv.tax.gst_rate} onChange={(e) => setField("tax.gst_rate", parseFloat(e.target.value || 0))} className="h-10 font-mono-num" disabled={!inv.tax.gst_applicable} />
                </Field>
                <ToggleRow label="TDS Applicable" checked={inv.tax.tds_applicable} onChange={(v) => setField("tax.tds_applicable", v)} />
                <Field label="TDS Rate (%)">
                  <Input type="number" min="0" max="100" step="0.5" value={inv.tax.tds_rate} onChange={(e) => setField("tax.tds_rate", parseFloat(e.target.value || 0))} className="h-10 font-mono-num" disabled={!inv.tax.tds_applicable} />
                </Field>
                <ToggleRow label="Reverse Charge" checked={inv.tax.reverse_charge} onChange={(v) => setField("tax.reverse_charge", v)} />
              </div>
            </Card>
            <Card title="Payment Details" icon={<Coins size={14} weight="bold" />}>
              <div className="grid grid-cols-1 gap-2">
                <Field label="Bank Name"><Input value={inv.payment.bank_name} onChange={(e) => setField("payment.bank_name", e.target.value)} /></Field>
                <Field label="Account Name"><Input value={inv.payment.account_name} onChange={(e) => setField("payment.account_name", e.target.value)} /></Field>
                <Field label="Account Number"><Input value={inv.payment.account_number} onChange={(e) => setField("payment.account_number", e.target.value)} /></Field>
                <Field label="IFSC"><Input value={inv.payment.ifsc} onChange={(e) => setField("payment.ifsc", e.target.value)} /></Field>
                <Field label="UPI ID"><Input value={inv.payment.upi_id} onChange={(e) => setField("payment.upi_id", e.target.value)} /></Field>
                <Field label="Payment Terms"><Textarea rows={2} value={inv.payment.terms} onChange={(e) => setField("payment.terms", e.target.value)} /></Field>
              </div>
            </Card>
          </div>

          {/* Authorised Signatory */}
          <Card title="Authorised Signatory" icon={<UserCircle size={14} weight="bold" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name">
                <Input value={inv.signatory.name}
                       onChange={(e) => setField("signatory.name", e.target.value)}
                       placeholder="e.g. Anupam Kumar"
                       data-testid="signatory-name" />
              </Field>
              <Field label="Designation">
                <Input value={inv.signatory.designation}
                       onChange={(e) => setField("signatory.designation", e.target.value)}
                       placeholder="e.g. Director · Authorized Signatory"
                       data-testid="signatory-designation" />
              </Field>
              <Field label="Date">
                <Input type="date"
                       value={inv.signatory.date}
                       onChange={(e) => setField("signatory.date", e.target.value)}
                       data-testid="signatory-date" />
              </Field>
              <Field label="Signature (PNG/JPG, max 2 MB)">
                <div className="flex items-center gap-2">
                  <Input type="file" accept="image/png,image/jpeg,image/webp"
                         onChange={(e) => onSignatureUpload(e.target.files?.[0])}
                         className="h-11 file:mr-2 file:px-2 file:py-1 file:rounded-md file:border file:border-[#D4AF37]/40 file:bg-[#FDF9EE] file:text-[#A87C0E] file:text-xs"
                         data-testid="signatory-upload" />
                  {inv.signatory.signature_data_url && (
                    <Button variant="ghost" size="icon"
                            onClick={() => setField("signatory.signature_data_url", "")}
                            title="Remove signature"
                            className="text-[#9CA3AF] hover:text-rose-500 h-9 w-9 shrink-0"
                            data-testid="signatory-clear">
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              </Field>
              {inv.signatory.signature_data_url && (
                <div className="md:col-span-2">
                  <Label className="kostra-data-label mb-1 block">Signature preview</Label>
                  <div className="rounded-lg border border-dashed border-[#D4AF37]/40 bg-[#FDF9EE]/40 p-3 inline-block">
                    <img src={inv.signatory.signature_data_url}
                         alt="Authorised signatory"
                         className="max-h-20 max-w-[260px] object-contain"
                         data-testid="signatory-preview" />
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* ============================== Side rail ============================== */}
        <div className="space-y-4">
          {/* Totals */}
          <div className="rounded-2xl border border-[#0A3B24]/20 bg-white p-5 shadow-sm sticky top-4">
            <div className="text-[10px] uppercase tracking-wider text-[#A87C0E] font-bold mb-2">Summary</div>
            <TotalRow label="Service Subtotal" value={fmtINR(totals.service_subtotal)} />
            <TotalRow label="Add'l Charges" value={fmtINR(totals.charges_subtotal)} />
            <TotalRow label="Subtotal" value={fmtINR(totals.subtotal)} accent />
            {inv.tax.gst_applicable && (
              <TotalRow label={`GST @ ${inv.tax.gst_rate}%`} value={fmtINR(totals.gst_amount)} />
            )}
            {inv.tax.tds_applicable && (
              <TotalRow label={`Less TDS @ ${inv.tax.tds_rate}%`} value={`− ${fmtINR(totals.tds_amount)}`} negative />
            )}
            <div className="mt-2 pt-2 border-t border-[#D4AF37]/40 flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-wider text-[#0A3B24] font-bold">Grand Total</span>
              <span className="font-mono-num font-bold text-xl text-[#0A3B24]" data-testid="invoice-grand-total">
                {fmtINR(totals.grand_total)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button onClick={saveInvoice} disabled={busy} data-testid="invoice-save-btn">
                <FloppyDisk size={14} className="mr-1" />{editingId ? "Update" : "Save"}
              </Button>
              <Button variant="outline" onClick={newInvoice}><Plus size={14} className="mr-1" />New</Button>
              <Button variant="outline" disabled={!editingId} onClick={previewPDF} data-testid="invoice-preview-btn">
                <Eye size={14} className="mr-1" />Preview
              </Button>
              <Button variant="outline" disabled={!editingId} onClick={() => download("pdf")} data-testid="invoice-export-pdf">
                <FilePdf size={14} className="mr-1" />PDF
              </Button>
              <Button variant="outline" disabled={!editingId} onClick={() => download("excel")} data-testid="invoice-export-excel" className="col-span-2">
                <FileXls size={14} className="mr-1" />Download Excel
              </Button>
            </div>
            {!editingId && (
              <div className="text-[10px] text-[#6B7280] italic mt-2 text-center">Save first to enable Preview & downloads</div>
            )}
          </div>

          {/* Saved list */}
          <div className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-[#0A3B24] font-bold mb-2 flex items-center justify-between">
              <span>Saved invoices</span>
              <span className="font-mono-num text-[#6B7280]">{saved.length}</span>
            </div>
            <div className="max-h-[60vh] overflow-y-auto space-y-2">
              {saved.length === 0 && <div className="text-xs text-[#6B7280] italic py-2">No saved invoices yet.</div>}
              {saved.map((it) => (
                <div key={it.id} className={`rounded-lg border p-2.5 text-xs ${
                  editingId === it.id ? "border-emerald-500 bg-emerald-50/40" : "border-[#E5E7EB] hover:border-[#D4AF37]"
                }`} data-testid={`saved-invoice-${it.invoice_number}`}>
                  <div className="flex items-center justify-between gap-1">
                    <div className="font-mono-num font-bold text-[#0A3B24] truncate">{it.invoice_number}</div>
                    <Badge variant="outline" className="text-[8px] border-[#A87C0E]/40 text-[#A87C0E] whitespace-nowrap">
                      {it.title || it.doc_type}
                    </Badge>
                  </div>
                  <div className="text-[#4B5563] truncate">{it.client_name || "(no client)"} · {it.project_name || ""}</div>
                  {it.parent_invoice_number && (
                    <div className="text-[10px] text-[#6B7280] mt-0.5">
                      ↳ from <span className="font-mono-num">{it.parent_invoice_number}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-1">
                    <Badge variant="outline" className="text-[9px] border-[#D4AF37] text-[#A87C0E]">{it.status}</Badge>
                    <span className="font-mono-num font-bold text-[#A87C0E]">{fmtINR(it.grand_total || 0)}</span>
                  </div>
                  <div className="flex gap-1 mt-2">
                    <Button size="sm" variant="outline" onClick={() => loadInvoice(it.id)}
                            className="h-7 text-[10px] flex-1" data-testid={`saved-open-${it.invoice_number}`}>
                      <Eye size={12} className="mr-1" />Open
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteInvoice(it.id)}
                            className="h-7 text-[10px] text-rose-500 hover:bg-rose-50"
                            data-testid={`saved-delete-${it.invoice_number}`}>
                      <Trash size={12} />
                    </Button>
                  </div>
                  {/* Module 11 — Quick clone actions */}
                  <div className="flex gap-1 mt-1 pt-1 border-t border-dashed border-[#E5E7EB]">
                    <button type="button"
                            onClick={() => cloneAs(it.id, "tax_invoice", "Tax Invoice")}
                            className="flex-1 h-6 rounded-md border border-[#0A3B24]/30 text-[#0A3B24] text-[9px] font-bold uppercase tracking-wider hover:bg-[#0A3B24] hover:text-white transition-colors"
                            title="Convert to Tax Invoice"
                            data-testid={`saved-clone-tax-${it.invoice_number}`}>
                      → Tax Invoice
                    </button>
                    <button type="button"
                            onClick={() => cloneAs(it.id, "credit_note", "Credit Note")}
                            className="flex-1 h-6 rounded-md border border-rose-300 text-rose-600 text-[9px] font-bold uppercase tracking-wider hover:bg-rose-500 hover:text-white transition-colors"
                            title="Generate Credit Note (flips amounts negative)"
                            data-testid={`saved-clone-credit-${it.invoice_number}`}>
                      Credit Note
                    </button>
                    <button type="button"
                            onClick={() => cloneAs(it.id, "reminder", "Payment Reminder")}
                            className="flex-1 h-6 rounded-md border border-amber-300 text-amber-700 text-[9px] font-bold uppercase tracking-wider hover:bg-amber-500 hover:text-white transition-colors"
                            title="Generate Payment Reminder"
                            data-testid={`saved-clone-reminder-${it.invoice_number}`}>
                      Reminder
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Small reusable bits
// =============================================================================
function Card({ title, icon, action, children }) {
  return (
    <section className="rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-[#0A3B24] inline-flex items-center gap-1.5">
          <span className="text-[#A87C0E]">{icon}</span>{title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children, className }) {
  return (
    <div className={className}>
      <Label className="kostra-data-label mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-[#0A3B24]">{label}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} />
    </div>
  );
}

function TotalRow({ label, value, accent, negative }) {
  return (
    <div className={`flex items-baseline justify-between py-1 text-sm ${accent ? "border-t border-[#E5E7EB] mt-1 pt-2 font-bold text-[#0A3B24]" : "text-[#4B5563]"} ${negative ? "text-amber-700" : ""}`}>
      <span>{label}</span>
      <span className="font-mono-num">{value}</span>
    </div>
  );
}
